import type { DataTableColumnDef } from 'components/tables/data-table';
import { FilterConfig } from 'components/tables/list-table/ListTableTypes';
import ListTableWithFilters from 'components/tables/list-table/ListTableWithFilters';
import { useMemo } from 'react';
import { useLazyGetTopCasesByBillableHoursReportQuery } from 'store/api/reportingExecutive';
import { TopCasesByBillableHoursRow, TopCasesByBillableHoursTotals } from 'types/reporting/executive';
import { ReportPage } from 'types/reporting/reportPage';
import { t } from 'utils/getTranslations';
import {
    ReportCaseLink,
    ReportContactLink,
    ReportDateCell,
    ReportEmployeeLink,
    ReportNumberCell,
    TONED_COLUMN_META,
    tonedCell,
    GREEN_OR_RED,
} from '../reportCells';
import { caseFilter, clientFilter, employeeFilter, periodFilter, periodQuickFilter } from '../reportFilters';
import { inBaseCurrency, useReportBaseCurrency } from '../reportCurrency';
import { ReportLayout } from '../ReportLayout';
import { toneLegendOf } from '../ReportToneLegend';
import { ReportRowLimitNote } from '../ReportRowLimitNote';
import { ReportEntry } from '../reportCatalog';

type Row = TopCasesByBillableHoursRow;
type Totals = TopCasesByBillableHoursTotals;

const REPORT_KEY = 'top-cases-by-billable-hours';

function buildFilterConfig(): FilterConfig {
    return {
        hideFilterDividers: true,
        quickFilterChips: true,
        periodQuickFilter: periodQuickFilter({ defaultPeriod: 'current-year' }),
        quickFilters: [],
        searchBarSelectors: [
            { selector: 'caseNumber', comparator: '=lk=' },
            { selector: 'caseName', comparator: '=lk=' },
            { selector: 'clientName', comparator: '=lk=' },
        ],
        filters: [
            periodFilter(),
            clientFilter(),
            caseFilter(),
            employeeFilter('supervisingPartnerId', 'reporting.filter.supervising-partner'),
            employeeFilter('responsibleLawyerId', 'reporting.filter.responsible-lawyer'),
        ],
    };
}

function buildColumns(rows: Row[] | undefined, currency: string | null): DataTableColumnDef<Row, Totals>[] {
    return [
        {
            id: 'clientNumber',
            accessorKey: 'clientNumber',
            header: t('reporting.column.client-number'),
            meta: { widthWeight: 0.5, minPx: 90 },
            cell: ({ row }) => (
                <ReportContactLink partyId={row.original.clientId}>{row.original.clientNumber}</ReportContactLink>
            ),
        },
        {
            id: 'clientName',
            accessorKey: 'clientName',
            header: t('reporting.column.client-name'),
            meta: { widthWeight: 1.2, minPx: 160 },
            cell: ({ row }) => (
                <ReportContactLink partyId={row.original.clientId}>{row.original.clientName}</ReportContactLink>
            ),
        },
        {
            id: 'caseNumber',
            accessorKey: 'caseNumber',
            header: t('reporting.column.case-number'),
            meta: { widthWeight: 0.6, minPx: 110 },
            cell: ({ row }) => (
                <ReportCaseLink matterId={row.original.matterId} wrapLabel>
                    {row.original.caseNumber}
                </ReportCaseLink>
            ),
        },
        {
            id: 'caseName',
            accessorKey: 'caseName',
            header: t('reporting.column.case-name'),
            meta: { widthWeight: 1.4, minPx: 180 },
            cell: ({ row }) => (
                <ReportCaseLink matterId={row.original.matterId}>{row.original.caseName}</ReportCaseLink>
            ),
        },
        {
            id: 'openedDate',
            accessorKey: 'openedDate',
            header: t('reporting.column.opened-date'),
            meta: { widthWeight: 0.6, minPx: 110 },
            cell: ({ row }) => <ReportDateCell value={row.original.openedDate} />,
        },
        {
            id: 'supervisingPartner',
            accessorKey: 'supervisingPartner',
            header: t('reporting.column.supervising-partner'),
            meta: { widthWeight: 0.9, minPx: 140 },
            cell: ({ row }) => (
                <ReportEmployeeLink employeeId={row.original.supervisingPartnerId}>
                    {row.original.supervisingPartner}
                </ReportEmployeeLink>
            ),
        },
        {
            id: 'billableHours',
            accessorKey: 'billableHours',
            header: t('reporting.column.billable-hours'),
            meta: {
                ...TONED_COLUMN_META,
                widthWeight: 0.8,
                minPx: 130,
                textAlign: 'right',
                nullHandling: 'nullsLast',
                renderTotal: (totals) => <ReportNumberCell value={totals?.billableHours} />,
            },
            cell: tonedCell(rows, (row) => row.billableHours, GREEN_OR_RED),
        },
        {
            id: 'billableAmount',
            accessorKey: 'billableAmount',
            header: inBaseCurrency(t('reporting.column.billable-amount'), currency),
            meta: {
                ...TONED_COLUMN_META,
                widthWeight: 0.9,
                minPx: 150,
                textAlign: 'right',
                nullHandling: 'nullsLast',
                renderTotal: (totals) => <ReportNumberCell value={totals?.billableAmount} />,
            },
            cell: tonedCell(rows, (row) => row.billableAmount, GREEN_OR_RED),
        },
    ];
}

export function TopCasesByBillableHoursReport({ report }: { report: ReportEntry }) {
    const [fetchData, { data, isFetching, isError }] = useLazyGetTopCasesByBillableHoursReportQuery();
    const filterConfig = useMemo(() => buildFilterConfig(), []);
    const currency = useReportBaseCurrency(data?.totals?.baseCurrency ?? data?.content[0]?.baseCurrency);
    const columns = useMemo(() => buildColumns(data?.content, currency), [data, currency]);
    return (
        <ReportLayout report={report} fillHeight toneLegend={toneLegendOf(columns)} baseCurrency={currency}>
            <ListTableWithFilters<Row, ReportPage<Row, Totals>>
                data={data}
                belowQuickFilters={<ReportRowLimitNote report={report} rowCount={data?.content.length} />}
                isLoading={isFetching}
                isError={isError}
                fetchData={fetchData}
                filterConfig={filterConfig}
                columns={columns}
                columnConfigId={`report-${REPORT_KEY}`}
                enableColumnPicker
                enableColumnReorder
                enableColumnResize
                pluralTextId="reporting.cases"
                singularTextId="reporting.case"
                noEntriesId="reporting.no-rows"
                noEntriesBodyId="reporting.no-rows-body"
            />
        </ReportLayout>
    );
}
